import React, { useEffect, useState } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";

export default function ReviewsSection({ limit = 6, title = "Hvad vores kunder siger", subtitle = "Anmeldelser" }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleItems, setVisibleItems] = useState(3);

  // Touch swipe state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  useEffect(() => {
    fetch("https://api.dev.auxo.dk/public/content/reviews")
      .then((res) => {
        if (!res.ok) throw new Error("Kunne ikke hente anmeldelser");
        return res.json();
      })
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setReviews(data);
        } else {
          setReviews(getFallbackReviews());
        }
      })
      .catch((err) => {
        console.error("Fejl ved hentning af anmeldelser:", err);
        setReviews(getFallbackReviews());
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Handle responsive visible items count
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setVisibleItems(1);
      } else if (window.innerWidth < 1024) {
        setVisibleItems(2);
      } else {
        setVisibleItems(3);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getFallbackReviews = () => [
    {
      name: "Lars Poulsen",
      rating: 5,
      comment: "Altid fantastisk smørrebrød! Højtbelagt, friskt og med masser af smag. Deres flæskestegssandwich er den bedste i Hillerød.",
      date: "12. maj 2026",
      source: "Google"
    },
    {
      name: "Gunay Cekic",
      rating: 5,
      comment: "En kæmpe anbefaling herfra! Hvis du ikke allerede har prøvet Hillerød Smørrebrød, så skylder du dig selv et besøg. Maden er helt i top – frisklavet, super lækkert og anrettet med sans for både smag og udseende.",
      date: "20. maj 2026",
      source: "Facebook"
    },
    {
      name: "Lone Wolf Windum",
      rating: 5,
      comment: "Et godt sted. Øzlem & Hallis kan deres håndværk.",
      date: "18. maj 2026",
      source: "Facebook"
    },
    {
      name: "Lene Vibe Jensen",
      rating: 4,
      comment: "Super lækre sandwich, dog kunne jeg bedre lide det brød i lavede før. Det nye er lidt for porøst til mig, men mine veninder var vilde med det.",
      date: "15. maj 2026",
      source: "Facebook"
    }
  ];

  const displayedReviews = reviews.slice(0, limit);
  const maxIndex = Math.max(0, displayedReviews.length - visibleItems);

  // Clamp current index if it exceeds maxIndex (e.g. after resize)
  useEffect(() => {
    if (currentIndex > maxIndex) {
      setCurrentIndex(maxIndex);
    }
  }, [maxIndex, currentIndex]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  // Touch handlers for swipe support
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  return (
    <section className="py-16 bg-neutral-50 border-y border-neutral-200">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#2d5a27] block mb-2">
            {subtitle}
          </span>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-neutral-900 tracking-tight">
            {title}
          </h2>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2d5a27]" />
          </div>
        ) : (
          <div className="relative">
            {/* Carousel Container */}
            <div className="overflow-hidden -mx-4 px-4 py-4">
              <div
                className="flex transition-transform duration-500 ease-out"
                style={{
                  transform: `translateX(-${currentIndex * (100 / visibleItems)}%)`,
                }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                {displayedReviews.map((review, idx) => (
                  <div
                    key={review.id || idx}
                    className="w-full md:w-1/2 lg:w-1/3 shrink-0 px-4 flex"
                  >
                    <div className="bg-white p-8 border border-neutral-200 shadow-sm flex flex-col justify-between relative hover:shadow-md transition-shadow duration-300 w-full">
                      <div className="relative z-10">
                        {/* Stars */}
                        <div className="flex gap-1 mb-4">
                          {[...Array(review.rating || 5)].map((_, i) => (
                            <Star
                              key={i}
                              size={16}
                              className="fill-[#2d5a27] text-[#2d5a27]"
                            />
                          ))}
                        </div>
                        {/* Comment */}
                        <p className="text-neutral-600 text-sm leading-relaxed italic mb-6">
                          "{review.comment}"
                        </p>
                      </div>

                      {/* Author Info */}
                      <div className="border-t border-neutral-100 pt-4 flex justify-between items-center relative z-10">
                        <div>
                          <h4 className="font-semibold text-neutral-900 text-sm">
                            {review.name}
                          </h4>
                          <span className="text-xs text-neutral-400">
                            {review.date || "Kunde"}
                          </span>
                        </div>
                        {review.source && (
                          <span className="text-[10px] font-bold uppercase tracking-wider bg-neutral-100 text-neutral-500 px-2 py-1 rounded">
                            {review.source}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Arrows */}
            {maxIndex > 0 && (
              <>
                <button
                  onClick={prevSlide}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-6 z-20 bg-white border border-neutral-200 p-3 rounded-full shadow-md hover:bg-neutral-50 hover:text-[#2d5a27] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#2d5a27]"
                  aria-label="Forrige anmeldelse"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-6 z-20 bg-white border border-neutral-200 p-3 rounded-full shadow-md hover:bg-neutral-50 hover:text-[#2d5a27] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#2d5a27]"
                  aria-label="Næste anmeldelse"
                >
                  <ChevronRight size={20} />
                </button>
              </>
            )}

            {/* Dot Indicators */}
            {maxIndex > 0 && (
              <div className="flex justify-center gap-2 mt-8">
                {[...Array(maxIndex + 1)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentIndex(i)}
                    className={`h-2 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#2d5a27] focus:ring-offset-2 ${
                      currentIndex === i
                        ? "w-6 bg-[#2d5a27]"
                        : "w-2 bg-neutral-300 hover:bg-neutral-400"
                    }`}
                    aria-label={`Gå til slide ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
